import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Bell,
  ChartLine,
  CheckCircle2,
  LayoutDashboard,
  MessageSquareWarning,
  PhoneIncoming,
  PhoneOutgoing,
  TimerReset,
  TrendingUp,
  X,
} from "lucide-react";
import { useContextStateManagement } from "../../context/ContextApi";
import { DEFAULT_PRACTICE_TIMEZONE } from "../../practiceTimezone";
import InfoTooltip from "../common/InfoTooltip";
import "./Homepage.css";

const formatCount = (value) => Number(value || 0).toLocaleString("en-US");

const formatMinutesValue = (mins) => {
  const total = Math.max(0, mins | 0);
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

// toISOString() reports the UTC date - anyone west of UTC rolls over to "tomorrow"
// hours before local midnight, silently dropping same-day calls from this fetch.
const todayDateOnly = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const BANNER_ANNOUNCEMENT_ID = "holiday-schedule-2026";
const BANNER_DISMISS_KEY = `cm_homepage_banner_dismissed_${BANNER_ANNOUNCEMENT_ID}`;

const quickLaunchCards = [
  {
    id: "inbound",
    title: "Inbound",
    icon: PhoneIncoming,
    to: "/?tab=inbound",
    sideBarLinkActive: "careConsole",
    activeTab: "inBoundCalls",
  },
  {
    id: "outbound",
    title: "Outbound",
    icon: PhoneOutgoing,
    to: "/?tab=outbound",
    sideBarLinkActive: "careConsole",
    activeTab: "leadGTab",
  },
  {
    id: "summary",
    title: "Summary",
    icon: ChartLine,
    to: "/?tab=summary",
    sideBarLinkActive: "careConsole",
    activeTab: "summaryTab",
  },
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    to: "/Dashboard",
    sideBarLinkActive: "dashboard",
  },
];

export const Homepage = () => {
  const navigate = useNavigate();
  const {
    closePhoneTab,
    setActiveTab,
    setSideBarLinkActive,
    PracticeId,
    token,
    usersPostRequest,
    outBoundContact,
    inboundContact,
  } = useContextStateManagement();
  const [bannerVisible, setBannerVisible] = useState(
    () => localStorage.getItem(BANNER_DISMISS_KEY) !== "true",
  );
  const [todayConversations, setTodayConversations] = useState([]);
  const [summaryError, setSummaryError] = useState("");
  // The practice's own configured timezone, resolved server-side alongside today's
  // conversations above - rendering their timestamps in this same zone (instead of the
  // browser's local zone) is what keeps "what day/time" a call falls on agreeing with the filter.
  const [practiceTimezone, setPracticeTimezone] = useState(DEFAULT_PRACTICE_TIMEZONE);

  // Today's Snapshot and Recent Activity below both derive from this one fetch - same
  // /api/chartsData endpoint Dashboard.jsx uses, scoped to just today instead of a
  // user-picked range, so this page never shows placeholder numbers.
  useEffect(() => {
    if (!PracticeId || !token) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const today = todayDateOnly();
        const data = await usersPostRequest(token, "/api/chartsData", {
          PracticeId,
          // memberListName only scopes a separate leads/license lookup this page doesn't use -
          // conversationsList (all this page reads) comes from practice-wide Twilio logs
          // regardless of which value is passed - so any real lead table name satisfies the
          // backend's required-field check without narrowing the result we actually care about.
          memberListName: "LeadGenerationUploadLead",
          outBoundContact,
          inboundContact,
          startDate: today,
          endDate: today,
        });

        if (cancelled) return;

        if (!data || typeof data !== "object" || !Array.isArray(data.conversationsList)) {
          setTodayConversations([]);
          setSummaryError("Could not load today's activity.");
        } else {
          setTodayConversations(data.conversationsList);
          setSummaryError("");
          if (data.practiceTimezone) {
            setPracticeTimezone(data.practiceTimezone);
          }
        }
      } catch (_) {
        if (!cancelled) {
          setTodayConversations([]);
          setSummaryError("Could not load today's activity.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // usersPostRequest intentionally excluded - ContextApi.js doesn't memoize its functions, so
    // including it would refetch on every unrelated context re-render instead of only when
    // PracticeId/token actually change (same rationale as ManageLeadsPage.js's
    // loadSchemaAndProviders).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, token, outBoundContact, inboundContact]);

  const todayMetrics = useMemo(() => {
    const total = todayConversations.length;
    const completed = todayConversations.filter((row) => row?.status === "completed").length;
    // Any attempt that wasn't a genuine connection counts as missed - matches the
    // lead.MissedCalls definition used elsewhere (services/index.js: total - completed -
    // voicemail), not just no_answer/busy. That narrower check silently excluded outbound
    // attempts logged as failed/canceled/other, understating this tile relative to what the
    // Outbound leads grid's own "Missed Call" column shows for the same activity.
    const missed = todayConversations.filter(
      (row) => row?.status !== "completed" && row?.status !== "voicemail",
    ).length;
    // Whole-call duration (the root/inbound leg's own span, start to end) - matches how
    // Inbound/Outbound Overview and the Dashboard's "Actual" card already calculate this,
    // so the number agrees across the app instead of only counting the AI+transfer portion.
    const actualSecondsTotal = todayConversations.reduce((sum, row) => {
      const legs = Array.isArray(row?.legs) ? row.legs : [];
      const rowActualSec = legs.length > 0
        ? legs
            .filter((leg) => leg?.legLevel === "root" || leg?.role === "root")
            .reduce((legSum, leg) => legSum + (Number(leg?.durationSec) || 0), 0)
        : Number(row?.durationSec) || 0;
      return sum + rowActualSec;
    }, 0);
    // Divided by completed, not total - missed/voicemail/failed attempts carry ~0 duration,
    // so averaging over every attempt understates how long calls that actually connected ran.
    const averageMinutes = completed ? Math.round(actualSecondsTotal / completed / 60) : 0;

    return [
      {
        label: "Total Calls",
        value: formatCount(total),
        helper: "Today",
        icon: TrendingUp,
        info: "All calls made or received today.",
      },
      {
        label: "Completed",
        value: formatCount(completed),
        helper: total ? `${Math.round((completed / total) * 100)}% completion` : "No calls yet",
        icon: CheckCircle2,
        info: "Calls that connected successfully today.",
      },
      {
        label: "Missed",
        value: formatCount(missed),
        helper: missed ? "Requires follow-up" : "None today",
        icon: MessageSquareWarning,
        info: "Incoming calls today that went unanswered.",
      },
      {
        label: "Avg Duration",
        value: completed ? formatMinutesValue(averageMinutes) : "0 min",
        helper: "Completed calls",
        icon: TimerReset,
        info: "Average length of today's completed calls.",
      },
    ];
  }, [todayConversations]);

  const recentActivity = useMemo(
    () =>
      [...todayConversations]
        .filter((row) => row?.startedAt)
        .sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))
        .slice(0, 5)
        .map((row, index) => {
          const directionLabel = row?.direction === "outbound" ? "Outbound" : "Inbound";
          const statusLabel =
            row?.status === "completed"
              ? "completed"
              : row?.status === "voicemail"
                ? "went to voicemail"
                : row?.status === "no_answer"
                  ? "was missed"
                  : row?.status === "failed"
                    ? "failed"
                    : "was logged";
          // Whole-call duration (the root/inbound leg's own span) - matches Inbound/Outbound
          // Overview and the Dashboard's "Actual" card, so Recent Activity's per-call time
          // agrees with the rest of the app.
          const legs = Array.isArray(row?.legs) ? row.legs : [];
          const actualSec = legs.length > 0
            ? legs
                .filter((leg) => leg?.legLevel === "root" || leg?.role === "root")
                .reduce((sum, leg) => sum + (Number(leg?.durationSec) || 0), 0)
            : Number(row?.durationSec) || 0;
          const actualMinutes = Math.max(0, Math.round(actualSec / 60));

          return {
            id: row?.callSid || row?.id || `activity-${index}`,
            timestamp: new Date(row.startedAt).toLocaleTimeString("en-US", {
              timeZone: practiceTimezone,
              hour: "numeric",
              minute: "2-digit",
            }),
            title: `${directionLabel} call ${statusLabel}`,
            detail:
              row?.status === "completed" && actualMinutes
                ? `${actualMinutes} min call`
                : row?.transferLegCount > 0
                  ? "Included a transfer"
                  : "",
          };
        }),
    [todayConversations, practiceTimezone],
  );

  const handleQuickLaunch = (card) => {
    closePhoneTab?.();
    setSideBarLinkActive(card.sideBarLinkActive);

    if (card.activeTab) {
      setActiveTab(card.activeTab);
    }

    navigate(card.to);
  };

  return (
    <div className="homepage-shell">
      {bannerVisible ? (
        <div className="homepage-banner" role="status" aria-live="polite">
          <div className="homepage-banner__copy">
            <Bell size={16} strokeWidth={2} />
            <span>
              Holiday schedule update: office hours may change on upcoming service days.
            </span>
          </div>
          <button
            type="button"
            className="homepage-banner__dismiss"
            onClick={() => {
              localStorage.setItem(BANNER_DISMISS_KEY, "true");
              setBannerVisible(false);
            }}
            aria-label="Dismiss announcement banner"
          >
            <X size={14} strokeWidth={2.4} />
          </button>
        </div>
      ) : null}

      <section className="homepage-panel homepage-panel--compact">
        <div className="homepage-panel__header">
          <div>
            <span className="homepage-panel__eyebrow">Homepage</span>
            <h1 className="homepage-title">Quick Launch</h1>
          </div>
        </div>

        <div className="homepage-quick-grid">
          {quickLaunchCards.map((card) => {
            const Icon = card.icon;

            return (
              <button
                key={card.id}
                type="button"
                className="homepage-quick-card"
                onClick={() => handleQuickLaunch(card)}
              >
                <span className="homepage-quick-card__icon">
                  <Icon size={18} strokeWidth={2} />
                </span>
                <span className="homepage-quick-card__body">
                  <strong>{card.title}</strong>
                </span>
                <span className="homepage-quick-card__action">
                  Open <ArrowRight size={14} strokeWidth={2.2} />
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="homepage-panel homepage-panel--compact">
        <div className="homepage-panel__header">
          <div>
            <span className="homepage-panel__eyebrow">Today's Snapshot</span>
            <h2>
              Today
              <InfoTooltip text="Summary of today's call activity." />
            </h2>
          </div>
        </div>

        {summaryError ? (
          <p className="homepage-panel__lede">{summaryError}</p>
        ) : (
          <div className="homepage-kpi-strip">
            {todayMetrics.map((metric) => {
              const Icon = metric.icon;

              return (
                <article className="homepage-kpi" key={metric.label}>
                  <div className="homepage-kpi__icon">
                    <Icon size={16} strokeWidth={2.1} />
                  </div>
                  <div className="homepage-kpi__copy">
                    <span>
                      {metric.label}
                      <InfoTooltip text={metric.info} />
                    </span>
                    <strong>{metric.value}</strong>
                    <em>{metric.helper}</em>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="homepage-panel homepage-panel--compact">
        <div className="homepage-panel__header">
          <div>
            <span className="homepage-panel__eyebrow">Recent Activity</span>
            <h2>
              Recent Activity
              <InfoTooltip text="Most recent calls today." />
            </h2>
          </div>
        </div>

        <div className="homepage-activity-list">
          {summaryError ? (
            <p className="homepage-panel__lede">{summaryError}</p>
          ) : recentActivity.length ? (
            recentActivity.map((item) => (
              <div key={item.id} className="homepage-activity-item">
                <div className="homepage-activity-item__time">{item.timestamp}</div>
                <div className="homepage-activity-item__body">
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                </div>
              </div>
            ))
          ) : (
            <p className="homepage-panel__lede">No calls yet today.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Homepage;
